import { useCallback, useEffect, useRef, useState } from 'react';
import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	Legend,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	ResponsiveContainer,
	LabelList,
	Line,
	LineChart,
	AreaChart,
	Area,
} from 'recharts';
import TableLoading from '../../../components/TableLoading';
import { notifyError } from '../../../services/notify';
import { capitalizeHyphenated, mapTimeRangeToApiFilterAlt3, request } from '../../../services/utilities';
import { analyticsAPI } from '../../../services/constants';
import SlidingPane from '../../../components/SlidingPane';
import { Card, Col, Row, Statistic } from 'antd';
import { formatDuration } from '../../../services/utilities';
import AnalysisBystaff from '../../../components/Modals/frontDesk/staffAnalyticsmodal';
import { TimeRangeDropdownAlt3 } from '../../../components/TimeRangeDropdownAlt3';
import { MetricCardAlt } from '../../../components/MetricCardAlt';
import { CustomTooltipAlt2 } from '../../Doctor/Overview';

const StaffLabOverview = ({ selectedStaff }) => {
	const [loading, setLoading] = useState({
		ageRange: false,
		patientAgeRange: false,
		genderRange: false,
		mostBookedCategory: false,
		tatHighest: false,
		tatAll: false,
		tatLowest: false,
		highestTechnicians: false,
		lowestTechnicians: false,
		inOutPatients: false,
		allLabTechnicians: false,
		metrics: false,
		staffRank: false,
	});

	const [timeRanges, setTimeRanges] = useState({
		metrics: 'past30Days',
		ageRange: 'past30Days',
		patientAgeRange: 'past30Days',
		genderRange: 'past30Days',
		mostBookedCategory: 'past30Days',
		tatHighest: 'past30Days',
		tatAll: 'past30Days',
		tatLowest: 'past30Days',
		highestTechnicians: 'past30Days',
		lowestTechnicians: 'past30Days',
		inOutPatients: 'past30Days',
		allLabTechnicians: 'past30Days',
		staffRank: 'past30Days',
	});

	const [ageRange, setAgeRange] = useState([]);
	const [patientAgeRange, setPatientAgeRange] = useState([]);
	const [genderRange, setGenderRange] = useState([]);
	const [tatHighest, setTatHighest] = useState([]);
	const [tatLowest, setTatLowest] = useState([]);
	const [highestLabTechnicians, setHighestLabTechnicians] = useState([]);
	const [lowestLabTechnicians, setLowestLabTechnicians] = useState([]);
	const [getTatAll, setGetTatAll] = useState([]);
	const [mostBookedCategory, setMostBookedCategory] = useState([]);
	const [inOutPatients, setInOutPatients] = useState([]);
	const [modal, setModal] = useState(false);
	const [modalAlt, setModalAlt] = useState(false);
	const [allLabTechnicians, setAllLabTechnicians] = useState([]);
	const [requestMetrics, setRequestMetrics] = useState();
	const [staffRank, setStaffRank] = useState([]);
	const [modalAlt2, setModalAlt2] = useState(false);

	const COLORS = [
		'#0088FE', // blue
		'#00C49F', // green
		'#FFBB28', // yellow
		'#FF8042', // orange
		'#8884d8', // purple
		'#FF6699', // pink
		'#A28EFF', // light purple
		'#33CCFF', // sky blue
		'#FF4444', // red
		'#22BB33',
	];

	const fetchRequestMetrics = async range => {
		setLoading(prev => ({ ...prev, metrics: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/patient-request-metrics?dateFilter=${dateFilter}&requestType=labs&staffId=${selectedStaff?.id}`;

			const res = await request(url, 'GET', true);

			setRequestMetrics(res?.result || {});
		} catch (error) {
			console.error('Failed to fetch request metrics:', error);
			// setRequestMetrics({});
		} finally {
			setLoading(prev => ({ ...prev, metrics: false }));
		}
	};

	const fetchMostBookedAgeRangeTest = async range => {
		setLoading(prev => ({ ...prev, ageRange: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/most-booked-by-age-range?dateFilter=${dateFilter}&requestType=labs&staffId=${selectedStaff?.id}`;

			const res = await request(url, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name: x?.name,
				count: parseInt(x?.count, 10),
				mostFrequentTest: x?.mostFrequentTest,
			}));

			setAgeRange(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch most booked age range:', error);
			setAgeRange([]);
		} finally {
			setLoading(prev => ({ ...prev, ageRange: false }));
		}
	};

	const fetchPatientAgeRangeTest = async range => {
		setLoading(prev => ({ ...prev, patientAgeRange: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/patients-by-age-range?dateFilter=${dateFilter}&requestType=labs&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name: x?.name,
				count: parseInt(x?.count, 10),
			}));
			setPatientAgeRange(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch patient age range:', error);
			setPatientAgeRange([]);
		} finally {
			setLoading(prev => ({ ...prev, patientAgeRange: false }));
		}
	};

	const fetchGenderRange = async range => {
		setLoading(prev => ({ ...prev, genderRange: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/appontment-gender?dateFilter=${dateFilter}&requestType=labs&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name: x?.gender,
				value: parseInt(x?.count, 10),
			}));
			setGenderRange(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch gender range:', error);
			setGenderRange([]);
		} finally {
			setLoading(prev => ({ ...prev, genderRange: false }));
		}
	};

	const fetchTATLowest = async range => {
		setLoading(prev => ({ ...prev, tatLowest: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/get-turn-around-time-ranking?dateFilter=${dateFilter}&requestType=labs&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.data?.result
				?.map(x => ({
					name: x?.testName,
					value: x?.avgTATMinutes || 0,
				}))
				.slice(0, 25);
			setTatLowest(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch lowest TAT:', error);
			setTatLowest([]);
		} finally {
			setLoading(prev => ({ ...prev, tatLowest: false }));
		}
	};

	const fetchTATAll = async range => {
		setLoading(prev => ({ ...prev, tatAll: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/get-turn-around-time-ranking?dateFilter=${dateFilter}&requestType=labs&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.data?.result?.map(x => ({
				name: x?.testName,
				value: x?.avgTATMinutes || 0,
			}));
			setGetTatAll(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch all TAT:', error);
			setGetTatAll([]);
		} finally {
			setLoading(prev => ({ ...prev, tatAll: false }));
		}
	};

	const fetchTATHighest = async range => {
		setLoading(prev => ({ ...prev, tatHighest: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/get-turn-around-time-ranking?dateFilter=${dateFilter}&requestType=labs&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.data?.result
				?.map(x => ({
					name: x?.testName,
					value: x?.avgTATMinutes || 0,
				}))
				.slice(0, 30);
			setTatHighest(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch highest TAT:', error);
			setTatHighest([]);
		} finally {
			setLoading(prev => ({ ...prev, tatHighest: false }));
		}
	};

	const fetchLabTechniciansHighest = async range => {
		setLoading(prev => ({ ...prev, highestTechnicians: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/get-technician-fill-ranking?dateFilter=${dateFilter}&requestType=labs&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.data?.result?.map(x => ({
				name: x?.filledBy,
				value: x?.fillCount || 0,
			}));
			setHighestLabTechnicians(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch highest technicians:', error);
			setHighestLabTechnicians([]);
		} finally {
			setLoading(prev => ({ ...prev, highestTechnicians: false }));
		}
	};

	const fetchAllLabTechnicians = async range => {
		setLoading(prev => ({ ...prev, allLabTechnicians: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/get-technician-fill-ranking?dateFilter=${dateFilter}&requestType=labs&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.data?.result?.map(x => ({
				name: x?.filledBy,
				value: x?.fillCount || 0,
			}));
			setAllLabTechnicians(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch all lab technicians:', error);
			setAllLabTechnicians([]);
		} finally {
			setLoading(prev => ({ ...prev, allLabTechnicians: false }));
		}
	};

	const fetchLabTechniciansLowest = async range => {
		setLoading(prev => ({ ...prev, lowestTechnicians: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/get-technician-fill-ranking?dateFilter=${dateFilter}&requestType=labs&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.data?.result?.map(x => ({
				name: x?.filledBy,
				value: x?.fillCount || 0,
			}));
			setLowestLabTechnicians(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch lowest technicians:', error);
			setLowestLabTechnicians([]);
		} finally {
			setLoading(prev => ({ ...prev, lowestTechnicians: false }));
		}
	};

	const fetchInOutPatients = async range => {
		setLoading(prev => ({ ...prev, inOutPatients: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/get-in-out-patient-count?dateFilter=${dateFilter}&requestType=labs&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.data?.map(x => ({
				name: x?.patientType,
				value: parseInt(x?.count, 10),
			}));
			setInOutPatients(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch in/out patients:', error);
			setInOutPatients([]);
		} finally {
			setLoading(prev => ({ ...prev, inOutPatients: false }));
		}
	};

	const fetchMostBookedCategory = async range => {
		setLoading(prev => ({ ...prev, mostBookedCategory: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/get-filled-requests-by-category?dateFilter=${dateFilter}&requestType=labs&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.data?.map(x => ({
				name: x?.mostBookedTest?.name || '',
				count: parseInt(x?.mostBookedTest?.count, 10),
			}));
			console.log(apiResponse, 'most-booked!');
			setMostBookedCategory(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch most booked category:', error);
			setMostBookedCategory([]);
		} finally {
			setLoading(prev => ({ ...prev, mostBookedCategory: false }));
		}
	};

	const fetchStaffRank = async range => {
		setLoading(prev => ({ ...prev, staffRank: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `analytics-v2/get-staff-request-count-by-role?roles=lab-manager&dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.map(x => ({
				...x,
				name: x?.username || 'unknown',
				role: x?.role,
			}));
			console.log(apiResponse, 'staff rank...');
			setStaffRank(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch staff rank:', error);
			setStaffRank([]);
		} finally {
			setLoading(prev => ({ ...prev, staffRank: false }));
		}
	};

	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));

		switch (chartId) {
			case 'metrics':
				fetchRequestMetrics(range);
				break;
			case 'ageRange':
				fetchMostBookedAgeRangeTest(range);
				break;
			case 'patientAgeRange':
				fetchPatientAgeRangeTest(range);
				break;
			case 'genderRange':
				fetchGenderRange(range);
				break;
			case 'mostBookedCategory':
				fetchMostBookedCategory(range);
				break;
			case 'tatHighest':
				fetchTATHighest(range);
				break;
			case 'tatAll':
				fetchTATAll(range);
				break;
			case 'tatLowest':
				fetchTATLowest(range);
				break;
			case 'highestTechnicians':
				fetchLabTechniciansHighest(range);
				break;
			case 'lowestTechnicians':
				fetchLabTechniciansLowest(range);
				break;
			case 'inOutPatients':
				fetchInOutPatients(range);
				break;
			case 'allLabTechnicians':
				fetchAllLabTechnicians(range);
				break;
			case 'staffRank':
				fetchStaffRank(range);
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		// const loadAll = async () => {
		// 	try {
		// 		await Promise.all([
		fetchRequestMetrics(timeRanges.requestMetrics);
		fetchMostBookedAgeRangeTest(timeRanges.ageRange);
		fetchPatientAgeRangeTest(timeRanges.patientAgeRange);
		fetchGenderRange(timeRanges.genderRange);
		fetchMostBookedCategory(timeRanges.requestCountFilter);
		fetchTATHighest(timeRanges.tatHighest);
		fetchTATAll(timeRanges.tatAll);
		fetchTATLowest(timeRanges.tatLowest);
		fetchLabTechniciansHighest(timeRanges.highestTechnicians);
		fetchLabTechniciansLowest(timeRanges.lowestTechnicians);
		fetchInOutPatients(timeRanges.inOutPatients);
		fetchAllLabTechnicians(timeRanges.allLabTechnicians);
		fetchStaffRank(timeRanges.staffRank);
		// 		]);
		// 	} catch (error) {
		// 		console.error('Error loading initial lab analytics:', error);
		// 	}
		// };

		// loadAll();
	}, []);

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	return (
		<div className="content-">
			<div className="content-bo">
				<AnalysisBystaff module="labs" slug="lab" />

				<div className="row">
					{/* Overview Metrics */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center w-100">
								<h6 className="element-header">Key Performance Indicator</h6>
								<div>
									<TimeRangeDropdownAlt3 value={timeRanges.metrics} onChange={handleTimeRangeChange} chartId="metrics" />
								</div>
							</div>

							<p className="chart-description mb-3 p-2 card">Summary metrics showing overall clinic activity and lab engagement levels.</p>
							<div className="element-content">
								<div className="">
									<div className="row">
										{loading.metrics ? (
											<div className="d-flex justify-content-center align-items-center w-100 element-box" style={{ minHeight: '200px' }}>
												<TableLoading />
											</div>
										) : (
											requestMetrics && (
												<div className="row">
													{requestMetrics &&
														typeof requestMetrics === 'object' &&
														Object.entries(requestMetrics).map(([key, value], idx) => {
															if (['success'].includes(key)) return null;

															const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

															const color = `hsl(${key.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360}, 70%, 50%)`;

															return (
																<MetricCardAlt
																	key={key}
																	metricKey={key}
																	label={label}
																	value={value}
																	total={requestMetrics?.totalRequests}
																	color={color}
																	currentRange={timeRanges.metrics}
																/>
															);
														})}
												</div>
											)
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Request Count */}
					<div className="col-sm-12 mt-4">
						<div className="element-wrapper">
							<div className="d-flex align-items-center justify-content-between">
								<h6 className="element-header">Request Count Overview</h6>
								<div classNam>
									<TimeRangeDropdownAlt3
										value={timeRanges.mostBookedCategory}
										onChange={handleTimeRangeChange}
										chartId="mostBookedCategory"
									/>
								</div>
							</div>

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart displays the total number of medical lab requests over a selected period, helping track demand and workload
								trends.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.mostBookedCategory ? (
											<TableLoading />
										) : mostBookedCategory.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<LineChart data={mostBookedCategory} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey="name"
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={2}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis />
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Line type="monotone" dataKey="count" stroke="#22BB33" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
															<LabelList
																dataKey="count"
																position="top"
																style={{ fontSize: '10px', fill: '#333' }}
																content={renderCustomLabelAlt}
															/>
														</Line>
													</LineChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold p-4">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/*Staff Rank */}
					<div className="col-12 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header">Staff Request Ranking</h6>

							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModalAlt2(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								&nbsp;
								<TimeRangeDropdownAlt3 chartId="staffRank" onChange={handleTimeRangeChange} value={timeRanges?.staffRank} />
							</div>

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								The Staff Request Ranking by Role chart highlights the distribution and frequency of staff requests across different job
								roles, helping identify which roles generate the highest number of requests based on the selected filter.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.staffRank ? (
											<TableLoading />
										) : staffRank ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer>
													<LineChart
														data={staffRank.slice(0, 30)}
														layout="horizontal"
														margin={{ top: 20, right: 20, left: 20, bottom: 55 }}
													>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey={'name'}
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={0}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip content={<CustomTooltipAlt2 />} />

														<Line
															type="monotone"
															dataKey="total"
															stroke="#22BB33"
															strokeWidth={2}
															dot={{ r: 2 }}
															activeDot={{ r: 4 }}
														></Line>
													</LineChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<SlidingPane isOpen={modalAlt2}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModalAlt2(false)}>
										&times;
									</button>
								</div>

								<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
									<TimeRangeDropdownAlt3 value={timeRanges.staffRank} onChange={handleTimeRangeChange} chartId="staffRank" />
								</div>
								<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">Staff Request Ranking</h6>
										</div>
									</div>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<p className="chart-description card p-2">
										The Staff Request Ranking by Role chart highlights the distribution and frequency of staff requests across different job
										roles, helping identify which roles generate the highest number of requests based on the selected filter.
									</p>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<div className=" card w-100 ">
										<div className="row">
											<div className="col-md-12 mb-4">
												<div className="card h-100">
													<div className="card-body">
														{loading.staffRank ? (
															<TableLoading />
														) : staffRank ? (
															<div style={{ width: '100%', height: 400 }}>
																<ResponsiveContainer>
																	<LineChart data={staffRank} layout="horizontal" margin={{ top: 20, right: 20, left: 20, bottom: 55 }}>
																		<CartesianGrid strokeDasharray="3 3" />
																		<XAxis
																			dataKey={'name'}
																			angle={-45}
																			textAnchor="end"
																			fontSize={12}
																			interval={2}
																			tickFormatter={value => {
																				const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																				const capitalized = truncated
																					.toLowerCase()
																					.split(' ')
																					.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																					.join(' ');
																				return capitalized;
																			}}
																		/>
																		<YAxis domain={[0, 'auto']} />
																		<Tooltip content={<CustomTooltipAlt2 />} />

																		<Line
																			type="monotone"
																			dataKey="total"
																			stroke="#22BB33"
																			strokeWidth={2}
																			dot={{ r: 2 }}
																			activeDot={{ r: 4 }}
																		></Line>
																	</LineChart>
																</ResponsiveContainer>
															</div>
														) : (
															<div className="text-center fs-bold">No Record Found!</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>

					{/*Age Range Distribution */}
					<div className="col-sm-6 mt-4">
						<div className="element-wrapper">
							<div className="d-flex align-items-center justify-content-between">
								<h6 className="element-header">Bookings by Age Range</h6>
								<div>
									<TimeRangeDropdownAlt3 value={timeRanges.ageRange} onChange={handleTimeRangeChange} chartId="ageRange" />
								</div>
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart shows the age groups with their number of medical lab bookings, helping identify key patient demographics
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.ageRange ? (
											<TableLoading />
										) : ageRange?.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<BarChart data={ageRange} layout="vertical" margin={{ top: 20, right: 60, left: 20, bottom: 40 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis type="number" />
														<YAxis
															dataKey="name"
															type="category"
															style={{ fontSize: '12px', fontWeight: 'normal', lineHeight: '5' }}
															interval={0}
															tickFormatter={value => (value.length > 20 ? value.substring(0, 20) + '...' : value)}
														/>
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Legend wrapperStyle={{ marginBottom: '-20px' }} name="Lab Test Count" />
														<Bar dataKey="count" fill="#0088FE" name="Lab Test Count">
															<LabelList
																dataKey={entry => entry.count.toLocaleString()}
																position="right"
																style={{ fontSize: '12px', fill: '#333' }}
															/>
														</Bar>
													</BarChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Patient Age Range Distribution */}
					<div className="col-sm-6 mt-4">
						<div className="element-wrapper">
							<div className="d-flex align-items-center justify-content-between">
								<h6 className="element-header">Patients By Age Range</h6>
								<div>
									<TimeRangeDropdownAlt3 value={timeRanges.patientAgeRange} onChange={handleTimeRangeChange} chartId="patientAgeRange" />
								</div>
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart provides a breakdown of patients by age range, offering valuable insights into demographic distribution and
								healthcare utilization trends based on selected date filters.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.patientAgeRange ? (
											<TableLoading />
										) : patientAgeRange.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer>
													<BarChart data={patientAgeRange} layout="vertical" margin={{ top: 20, right: 40, left: 20, bottom: 40 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis type="number" domain={[0, 'auto']} />
														<YAxis
															dataKey="name"
															type="category"
															style={{ fontSize: '12px', fontWeight: 'normal', lineHeight: '5' }}
															interval={0}
															tickFormatter={value => (value.length > 20 ? value.substring(0, 20) + '...' : value)}
														/>
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Legend wrapperStyle={{ marginBottom: '-20px' }} />
														<Bar dataKey="count" fill="#0088FE" name="Test Count">
															<LabelList
																dataKey={entry => entry.count.toLocaleString()}
																position="right"
																style={{ fontSize: '12px', fill: '#333' }}
															/>
														</Bar>
													</BarChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/*Average TAT Test Highest*/}
					<div className="col-sm-12 mt-4">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between w-100">
								<h6 className="element-header">Average TAT Test(Top)</h6>
								<div className="d-flex align-items-center justify-content-between">
									<div className="input-group input-group-sm" style={{ width: 'auto' }}>
										<div>
											<div
												style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
												onClick={() => setModal(true)}
												className="form-control form-control-sm text-white bg-primary rounded "
											>
												View All
											</div>
										</div>
										<div>
											<TimeRangeDropdownAlt3 value={timeRanges.tatHighest} onChange={handleTimeRangeChange} chartId="tatHighest" />
										</div>
									</div>
								</div>
							</div>

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This shows the top medical lab tests with the fastest average turnaround time (TAT)
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.tatHighest ? (
											<TableLoading />
										) : tatHighest.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<AreaChart data={tatHighest} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey="name"
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={2}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => formatDuration(value)}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Legend wrapperStyle={{ marginBottom: '-40px' }} />

														<Area
															type="monotone"
															dataKey="value"
															stroke="#22BB33"
															strokeWidth={2}
															fill="#22BB33"
															fillOpacity={0.3}
															activeDot={{ r: 6 }}
														>
															<LabelList
																dataKey="value"
																position="top"
																content={({ value, x, y }) => (
																	<text x={x} y={y - 5} fill="#333" fontSize={12} textAnchor="middle">
																		{formatDuration(value)}
																	</text>
																)}
																style={{ fontSize: '12px', fill: '#333' }}
															/>
														</Area>
													</AreaChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold p-4">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/*Average TAT Test Lowest */}
					<div className="col-sm-12 mt-4">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between w-100">
								<h6 className="element-header">Average TAT Test(Bottom)</h6>
								<div className="d-flex align-items-center justify-content-between">
									<div className="input-group input-group-sm" style={{ width: 'auto' }}>
										<div>
											<div
												style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
												onClick={() => setModal(true)}
												className="form-control form-control-sm text-white bg-primary rounded "
											>
												View All
											</div>
										</div>
										<div>
											<TimeRangeDropdownAlt3 value={timeRanges.tatLowest} onChange={handleTimeRangeChange} chartId="tatLowest" />
										</div>
									</div>
								</div>
							</div>

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								The chart below highlights the bottom average turnaround times for tests, showcasing top-performing processes and efficiency
								benchmarks based on selected date filters
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.tatLowest ? (
											<TableLoading />
										) : tatLowest.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<AreaChart data={tatLowest} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey="name"
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={2}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => formatDuration(value)}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Legend wrapperStyle={{ marginBottom: '-40px' }} />

														<Area
															type="monotone"
															dataKey="value"
															stroke="#FFD700" // yellow stroke
															strokeWidth={2}
															fill="#FFD700" // yellow fill
															fillOpacity={0.3} // transparency for softer look
															activeDot={{ r: 6 }}
														>
															<LabelList
																dataKey="value"
																position="top"
																content={({ value, x, y }) => (
																	<text x={x} y={y - 5} fill="#333" fontSize={12} textAnchor="middle">
																		{formatDuration(value)}
																	</text>
																)}
																style={{ fontSize: '12px', fill: '#333' }}
															/>
														</Area>
													</AreaChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold p-4">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>

						<SlidingPane isOpen={modal}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModal(false)}>
										&times;
									</button>
								</div>
								<div className="row w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">Average TAT Test(All)</h6>
											<div className="d-flex justify-content-end w-100">
												<TimeRangeDropdownAlt3 value={timeRanges.tatAll} onChange={handleTimeRangeChange} chartId="tatAll" />
											</div>

											<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
												High-level metrics showing overall metrics of reading over time.
											</p>
											<div className="element-box">
												<Row gutter={16}>
													<Col span={8}>
														<Card>
															<Statistic title="Total TAT Readings" value={getTatAll.length} suffix="" />
															<small className="text-muted">
																Total TAT for <span className=" bold">{mapTimeRangeToApiFilterAlt3(timeRanges.tatAll)}</span>
															</small>
														</Card>
													</Col>
													<Col span={8}>
														<Card>
															<Statistic
																title="Total TAT Readings"
																// value={allRanking?.filter(x => x?.type === 'registration').length}
																suffix=""
															/>
															<small className="text-muted">Total TAT for all time</small>
														</Card>
													</Col>
													<Col span={8}>
														<Card>
															{/* <Statistic title="Total Transactions" value={transSummary?.totalTransactions} suffix="" /> */}
															{/* <Statistic
																					title="Survey Coverage Rate"
																					value={coverageRate > 0 ? (coverageRate).toFixed(1) : 0}
																					suffix=""
																				/> */}
															<small className="text-muted">Cumulative Transactions From --- to --</small>
														</Card>
													</Col>
													{/* <Col span={6}>
																		<Card>
																			<Statistic title="Lowest Scoring Area" value={0} suffix="/3" />
																			<small className="text-muted">Needs attention</small>
																		</Card>
																	</Col> */}
												</Row>
											</div>
										</div>
									</div>
								</div>

								<div
									className=" w-100 d-flex justify-content-center align-items-center flex-column"
									style={{ paddingLeft: '50px', paddingRight: '50px' }}
								>
									<p className="chart-description card p-2 w-100" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
										The chart highlights the bottom average turnaround times for tests, showcasing top-performing processes and efficiency
										benchmarks based on selected date filters
									</p>
								</div>

								<div className="" style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className=" card w-100 p-5">
										{/* {console.log(getTatAll, 'getAllTatChart-----------------------------')} */}
										{loading.tatAll ? (
											<TableLoading />
										) : (
											<div>
												{getTatAll.length <= 25 ? (
													<ResponsiveContainer width="100%">
														<BarChart data={getTatAll} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
															<CartesianGrid strokeDasharray="3 3" />
															<XAxis
																dataKey="name"
																angle={-45}
																textAnchor="end"
																fontSize={12}
																interval={0}
																tickFormatter={value => {
																	const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																	const capitalized = truncated
																		.toLowerCase()
																		.split(' ')
																		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																		.join(' ');
																	return capitalized;
																}}
															/>
															<YAxis domain={[0, 'auto']} />
															<Tooltip
																formatter={value => Number(value).toLocaleString()}
																labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
															/>
															<Legend wrapperStyle={{ marginBottom: '-40px' }} />

															<Bar dataKey="value" barSize={40}>
																{getTatAll.map((entry, index) => (
																	<Cell
																		key={`cell-${index}`}
																		fill={['#FFD700', '#82ca9d', '#8884d8', '#ff7f50', '#00C49F'][index % 5]} // cycle through colors
																	/>
																))}

																<LabelList
																	dataKey="value"
																	position="top"
																	content={({ value, x, y }) => (
																		<text x={x} y={y - 5} fill="#333" fontSize={12} textAnchor="middle">
																			{value.toLocaleString()} min
																		</text>
																	)}
																	style={{ fontSize: '12px', fill: '#333' }}
																/>
															</Bar>
														</BarChart>
													</ResponsiveContainer>
												) : (
													<ResponsiveContainer width="100%" height={getTatAll.length * 40}>
														<BarChart layout="vertical" data={getTatAll} margin={{ top: 20, right: 30, left: 100, bottom: 20 }}>
															<CartesianGrid strokeDasharray="3 3" />

															{/* Value axis (X-axis) */}
															<XAxis type="number" domain={[0, 'auto']} />

															{/* Category axis (Y-axis) */}
															<YAxis
																dataKey="name"
																type="category"
																width={100}
																tickFormatter={value => {
																	const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																	const capitalized = truncated
																		.toLowerCase()
																		.split(' ')
																		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																		.join(' ');
																	return capitalized;
																}}
															/>

															<Tooltip
																formatter={value => Number(value).toLocaleString()}
																labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
															/>
															<Legend />

															<Bar dataKey="value" barSize={30}>
																{getTatAll.map((entry, index) => (
																	<Cell key={`cell-${index}`} fill={['#FFD700', '#82ca9d', '#8884d8', '#ff7f50', '#00C49F'][index % 5]} />
																))}
																{/* <LabelList
															dataKey="value"
															position="right"
															content={({ value, x, y }) => (
															<text
																x={x + 5}
																y={y}
																fill="#333"
																fontSize={12}
																textAnchor="start"
																dy={4}
															>
																{value.toLocaleString()} min
															</text>
															)}
														/> */}
															</Bar>
														</BarChart>
													</ResponsiveContainer>
												)}
											</div>
										)}
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>

					{/*Test Per Lab Technician(Highest) */}
					<div className="col-sm-12 mt-4">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between w-100">
								<h6 className="element-header">Test Per Lab Technician(Highest)</h6>
								<div className="d-flex align-items-center justify-content-between">
									<div className="input-group input-group-sm" style={{ width: 'auto' }}>
										<div>
											<div
												style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
												onClick={() => setModalAlt(true)}
												className="form-control form-control-sm text-white bg-primary rounded"
											>
												View All
											</div>
										</div>
										<div>
											<TimeRangeDropdownAlt3
												value={timeRanges.highestTechnicians}
												onChange={handleTimeRangeChange}
												chartId="highestTechnicians"
											/>
										</div>
									</div>
								</div>
							</div>

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								The chart identifies the lab technicians with the highest number of tests processed, offering insights into performance and
								workload distribution based on selected date filters
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.highestTechnicians ? (
											<TableLoading />
										) : highestLabTechnicians.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<LineChart data={highestLabTechnicians} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey="name"
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={2}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Legend wrapperStyle={{ marginBottom: '-40px' }} />
														<Line type="monotone" dataKey="value" stroke="#00C49F" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
															<LabelList
																dataKey="value"
																position="top"
																style={{ fontSize: '10px', fill: '#333' }}
																content={renderCustomLabelAlt}
															/>
														</Line>
													</LineChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold p-4">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/*Test Per Lab Technician(Lowest) */}
					<div className="col-sm-12 mt-4">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between w-100">
								<h6 className="element-header">Test Per Lab Technician(Lowest)</h6>
								<div className="d-flex align-items-center justify-content-between">
									<div className="input-group input-group-sm" style={{ width: 'auto' }}>
										<div>
											<div
												style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
												onClick={() => setModalAlt(true)}
												className="form-control form-control-sm text-white bg-primary rounded"
											>
												View All
											</div>
										</div>
										<div>
											<TimeRangeDropdownAlt3
												value={timeRanges.lowestTechnicians}
												onChange={handleTimeRangeChange}
												chartId="lowestTechnicians"
											/>
										</div>
									</div>
								</div>
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								The chart highlights the lab technicians with the lowest number of tests processed, providing insights into workload
								distribution and potential support needs based on selected date filters.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.lowestTechnicians ? (
											<TableLoading />
										) : lowestLabTechnicians.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<AreaChart data={lowestLabTechnicians} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey="name"
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={3}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Legend wrapperStyle={{ marginBottom: '-40px' }} />

														<Area
															type="monotone"
															dataKey="value"
															stroke="#3399FF" // blue stroke
															strokeWidth={2}
															fill="#3399FF" // blue fill
															fillOpacity={0.3} // transparency
															activeDot={{ r: 6 }}
														>
															<LabelList dataKey="value" position="top" style={{ fontSize: '10px', fill: '#333' }} />
														</Area>
														{/* <Area
															type="monotone"
															dataKey="value"
															stroke="#3399FF"
															strokeWidth={2}
															fill="#3399FF"
															fillOpacity={0.3}
															dot={{ r: 4, strokeWidth: 1.5, fill: '#ffffff', stroke:"#3399FF"  }}
															activeDot={{ r: 6 }}
														/> */}
													</AreaChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold p-4">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>

						<SlidingPane isOpen={modalAlt}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModalAlt(false)}>
										&times;
									</button>
								</div>
								<div className="row w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<div className=" d-flex justify-content-between align-items-center">
												<h6 className="element-header">Test Per Lab Technician(All)</h6>
												<div className="d-flex justify-content-end w-100">
													<TimeRangeDropdownAlt3
														value={timeRanges.allLabTechnicians}
														onChange={handleTimeRangeChange}
														chartId="allLabTechnicians"
													/>
												</div>
											</div>

											<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
												High-level metrics showing overall metrics of reading over time.
											</p>
											<div className="element-box">
												{/* <TableLoading /> */}

												<Row gutter={16}>
													<Col span={8}>
														<Card>
															<Statistic title="Total Tests" value={allLabTechnicians.length} suffix="" />
															<small className="text-muted">
																Total Test for {mapTimeRangeToApiFilterAlt3(timeRanges.allLabTechnicians)}
															</small>
														</Card>
													</Col>
													<Col span={8}>
														<Card>
															<Statistic
																title="Total Readings"
																// value={allRanking?.filter(x => x?.type === 'registration').length}
																suffix=""
															/>
															<small className="text-muted">Total Test for all time</small>
														</Card>
													</Col>
													<Col span={8}>
														<Card>
															{/* <Statistic title="Total Transactions" value={transSummary?.totalTransactions} suffix="" /> */}
															{/* <Statistic
																					title="Survey Coverage Rate"
																					value={coverageRate > 0 ? (coverageRate).toFixed(1) : 0}
																					suffix=""
																				/> */}
															<small className="text-muted">Cumulative Transactions From --- to --</small>
														</Card>
													</Col>
													{/* <Col span={6}>
																		<Card>
																			<Statistic title="Lowest Scoring Area" value={0} suffix="/3" />
																			<small className="text-muted">Needs attention</small>
																		</Card>
																	</Col> */}
												</Row>
											</div>
										</div>
									</div>
								</div>

								<div
									className=" w-100 d-flex justify-content-center align-items-center flex-column"
									style={{ paddingLeft: '50px', paddingRight: '50px' }}
								>
									<p className="chart-description card p-2 w-100" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
										The chart highlights the lab technicians with their number of tests processed, providing insights into workload
										distribution and potential support needs based on selected date filters.
									</p>
								</div>

								<div className="" style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									{/* {console.log(LabTechniciansChart, 'abTechniciansChart-----------------------------')} */}
									<div className=" card w-100 p-5">
										{loading.allLabTechnicians ? (
											<TableLoading />
										) : (
											<ResponsiveContainer width="100%" height={400}>
												<AreaChart data={allLabTechnicians} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
													<CartesianGrid strokeDasharray="3 3" />
													<XAxis
														dataKey="name"
														angle={-45}
														textAnchor="end"
														fontSize={12}
														interval={0}
														tickFormatter={value => {
															const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
															const capitalized = truncated
																.toLowerCase()
																.split(' ')
																.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																.join(' ');
															return capitalized;
														}}
													/>
													<YAxis domain={[0, 'auto']} />
													<Tooltip
														formatter={value => Number(value).toLocaleString()}
														labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
													/>
													<Legend wrapperStyle={{ marginBottom: '-40px' }} />

													<Area
														type="monotone"
														dataKey="value"
														stroke="#3399FF" // blue stroke
														strokeWidth={2}
														fill="#3399FF" // blue fill
														fillOpacity={0.3} // transparency
														activeDot={{ r: 6 }}
													>
														<LabelList dataKey="value" position="top" style={{ fontSize: '10px', fill: '#333' }} />
													</Area>
												</AreaChart>
											</ResponsiveContainer>
										)}
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>

					{/*Out and In patient Test Ratio */}
					<div className="col-sm-8 mt-4">
						<div className="element-wrapper">
							<div className="d-flex align-items-center justify-content-between">
								<h6 className="element-header">Out-Patient vs In-Patient Test Ratio</h6>
								<div>
									<TimeRangeDropdownAlt3 value={timeRanges.inOutPatients} onChange={handleTimeRangeChange} chartId="inOutPatients" />
								</div>
							</div>

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart presents the ratio of tests conducted for in-patients versus out-patients, offering insights into patient type
								distribution and healthcare service usage based on selected date filters.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											<div className="d-flex justify-content-center mt-4">
												{loading.inOutPatients ? (
													<TableLoading />
												) : inOutPatients.length > 0 ? (
													<div style={{ width: '100%', height: 400 }}>
														<ResponsiveContainer>
															{console.log(inOutPatients, '-------------------')}
															<PieChart width={500} height={300}>
																<Pie
																	data={inOutPatients}
																	cx="50%"
																	cy="50%"
																	// label
																	style={{ textTransform: 'capitalize' }}
																	label={({ name, value }) => `${capitalizeHyphenated(name)}: ${value}`}
																	labelLine
																	outerRadius={80}
																	fill="#8884d8"
																	dataKey="value"
																>
																	{inOutPatients?.map((entry, index) => (
																		<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
																	))}
																</Pie>
																<Tooltip formatter={(value, name) => [Number(value).toLocaleString(), capitalizeHyphenated(name)]} />
																<Legend formatter={value => capitalizeHyphenated(value)} />
															</PieChart>
														</ResponsiveContainer>
													</div>
												) : (
													<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
														<p className=" mt-6" style={{ paddingTop: '50px' }}>
															No Record for the selected time
														</p>
													</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default StaffLabOverview;
